import React, { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import StepShell from './StepShell';
import StepTitleWithInfo from './StepTitleWithInfo';
import type { FlowCtx } from './types';

interface Props {
  ctx: FlowCtx;
}

const shake = (el: HTMLDivElement | null) => {
  if (!el) return;
  el.classList.add('dcf-shake');
  setTimeout(() => el.classList.remove('dcf-shake'), 1500);
};

const CHECK_INTERVAL = 1600;
const REDIRECT_DELAY = 700;

/** Mobile data icon — signal bars with diagonal slash (teal, matching Lovable design) */
const MobileDataOffIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2" y="17" width="3.5" height="5" rx="0.8" fill="#217C70" />
    <rect x="7.5" y="12" width="3.5" height="10" rx="0.8" fill="#217C70" />
    <rect x="13" y="7" width="3.5" height="15" rx="0.8" fill="#217C70" />
    <rect x="18.5" y="2" width="3.5" height="20" rx="0.8" fill="#217C70" />
    <line x1="3" y1="3" x2="21" y2="21" stroke="#217C70" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

/** Location pin icon (tdesign:location-filled style) */
const LocationIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 34 34" fill="none" aria-hidden="true">
    <path d="M17 3C11.477 3 7 7.477 7 13c0 5.523 3.5 11.1 7.5 16.05.7.87 1.38 1.7 1.5 1.85a1.25 1.25 0 001.999 0c.12-.15.8-.98 1.5-1.85C23.5 24.1 27 18.523 27 13c0-5.523-4.477-10-10-10zm0 13.5a3.5 3.5 0 110-7 3.5 3.5 0 010 7z" fill="#217C70" />
  </svg>
);

/** VPN / globe with padlock badge (teal, matching Lovable design) */
const VpnIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <span
    style={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
    }}
    aria-hidden="true"
  >
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0d6e63"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9S9.5 5.5 12 3z" />
    </svg>
    <svg
      style={{
        position: 'absolute',
        top: -2,
        right: -3,
        width: 11,
        height: 11,
        background: '#ffffff',
        borderRadius: 3,
        padding: 1,
        boxSizing: 'border-box',
      }}
      viewBox="0 0 24 24"
      fill="#0d6e63"
    >
      <path d="M17 9V7a5 5 0 0 0-10 0v2H5v12h14V9h-2zM9 7a3 3 0 0 1 6 0v2H9V7z" />
    </svg>
  </span>
);

const Step2PreflightChecks: React.FC<Props> = ({ ctx }) => {
  // activeIndex moves from 0 → checks.length; each tick "evaluates" the next check
  const [activeIndex, setActiveIndex] = useState(0);

  const checks = [
    {
      key: 'mobile',
      ref: ctx.mobileDataRef,
      passing: !ctx.isMobileDataOn,
      title: 'Turn off Mobile Data',
      doneTitle: 'Mobile Data is off',
      body:
        'Disable cellular data to prevent your phone from automatically switching networks during the setup process.',
      onClick: undefined as (() => void) | undefined,
      Icon: MobileDataOffIcon,
    },
    // Location only appears here when it hasn't been granted — i.e. the user
    // tapped "Deny" on the Step 1 popup or permission was never given. Once
    // granted (reported via SYSTEM_STATUS), this card drops out.
    ...(!ctx.isLocpermiGiven
      ? [
          {
            key: 'location',
            ref: ctx.locationPermissionRef,
            passing: ctx.isLocpermiGiven,
            title: 'Allow Location Access',
            doneTitle: 'Location permission granted',
            body:
              'Required for Wi-Fi scanning and to detect your current Wi-Fi network. We never share your precise location data.',
            onClick: ctx.handleEnableLocation as (() => void) | undefined,
            Icon: LocationIcon,
          },
        ]
      : []),
    {
      key: 'vpn',
      ref: ctx.vpnRef,
      passing: !ctx.isVpnOn,
      title: 'Turn off VPN',
      doneTitle: 'VPN is off',
      body:
        'Active VPN connections can interfere with local network communication between your phone and the SmartEco IAQ device.',
      onClick: undefined,
      Icon: VpnIcon,
    },
  ];

  const total = checks.length;

  useEffect(() => {
    if (activeIndex < total) {
      const t = setTimeout(
        () => setActiveIndex(activeIndex + 1),
        CHECK_INTERVAL,
      );
      return () => clearTimeout(t);
    }
    if (checks.every((c) => c.passing)) {
      const t = setTimeout(() => {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ action: 'GET_CONNECTED_WIFI' }),
        );
        ctx.goTo('wificonf');
      }, REDIRECT_DELAY);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, ctx.isLocpermiGiven, ctx.isMobileDataOn, ctx.isVpnOn]);

  const evaluated = activeIndex >= total;
  const anyFailing = checks.some((c) => !c.passing);
  const showContinue = evaluated && anyFailing;

  const onContinue = () => {
    if (ctx.isMobileDataOn) shake(ctx.mobileDataRef.current);
    if (!ctx.isLocpermiGiven) shake(ctx.locationPermissionRef.current);
    if (ctx.isVpnOn) shake(ctx.vpnRef.current);

    if (anyFailing) return;
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'GET_CONNECTED_WIFI' }),
    );
    ctx.goTo('wificonf');
  };

  return (
    <StepShell
      stepNumber={2}
      onBack={ctx.openExitModal}
      secondary={{ label: 'Back', onClick: () => ctx.goTo('on') }}
      primary={{ label: 'Continue', onClick: onContinue, disabled: !evaluated }}
    >
      <StepTitleWithInfo>
        <h2 className="dcf-step-title">
          Step 2 of 4:{' '}
          {showContinue
            ? 'Complete the following steps to continue'
            : "Let's get your phone ready"}
        </h2>
      </StepTitleWithInfo>
      <div className="dcf-step-bar"></div>

      {checks.map((c, i) => {
        let state: 'pending' | 'checking' | 'done' | 'failed';
        if (i > activeIndex) state = 'pending';
        else if (i === activeIndex) state = 'checking';
        else state = c.passing ? 'done' : 'failed';

        const isInteractive = state === 'failed' && !!c.onClick;
        const Icon = c.Icon;

        return (
          <div
            key={c.key}
            ref={c.ref}
            className={`dcf-card dcf-card--row dcf-card--${state}`}
            onClick={isInteractive ? c.onClick : undefined}
            style={isInteractive ? { cursor: 'pointer' } : undefined}
          >
            <div className="dcf-card__step-number">{i + 1}</div>
            <div className="dcf-card__content">
              <div className="dcf-card__title-row">
                <h3 className="dcf-card__title">
                  {state === 'done' ? c.doneTitle : c.title}
                </h3>
                {(state === 'checking' || state === 'done') && (
                  <div className="dcf-card__title-icon">
                    {state === 'done' && <Check size={18} strokeWidth={3} />}
                    {state === 'checking' && (
                      <Loader2 size={18} className="dcf-spin" />
                    )}
                  </div>
                )}
              </div>
              <p className="dcf-card__body">{c.body}</p>
            </div>
            {(state === 'pending' || state === 'failed') && (
              <span className="dcf-card__trailing-icon">
                <Icon size={20} />
              </span>
            )}
          </div>
        );
      })}
    </StepShell>
  );
};

export default Step2PreflightChecks;
